import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { lazy, Suspense, useEffect, useLayoutEffect, useState } from "react";
import axios from "axios";

// ─── Above-the-fold: keep eager so the homepage renders instantly ─────
import HomePage from "@/pages/HomePage";
import CartAddedToast from "@/components/CartAddedToast";
import { Toaster } from "sonner";
import SiteLiveGate from "@/components/SiteLiveGate";
import { ensureLogoManifest } from "@/lib/logoManifest";
import { publicData } from '@/lib/publicData';
import { trackPageView } from '@/lib/metaPixel';
import { RouteSEO } from '@/components/RouteSEO';
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

// ─── Below-the-fold + secondary routes: lazy-loaded so the initial
//     JS bundle for the homepage stays small. Each route ships as its
//     own chunk that only downloads when the user actually navigates.
//     Prior state: ALL 27 pages were bundled together (~1MB of gzipped
//     JS shipped to every anonymous visitor even if they never left "/").
//     After this split, the homepage-only visitor gets ~200-300KB. ─────
const ShopPage = lazy(() => import("@/pages/ShopPage"));
const ProductPage = lazy(() => import("@/pages/ProductPage"));
const MachineLocatorPage = lazy(() => import("@/pages/MachineLocatorPage"));
const CartPage = lazy(() => import("@/pages/CartPage"));
const CheckoutPage = lazy(() => import("@/pages/CheckoutPage"));
const BalancePayPage = lazy(() => import("@/pages/BalancePayPage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const RegisterPage = lazy(() => import("@/pages/RegisterPage"));
const AccountPage = lazy(() => import("@/pages/AccountPage"));
const OrderSuccessPage = lazy(() => import("@/pages/OrderSuccessPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const POSStandalonePage = lazy(() => import("@/pages/POSStandalonePage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const ProductRequestPage = lazy(() => import("@/pages/ProductRequestPage"));
const BrandsPage = lazy(() => import("@/pages/BrandsPage"));
const PreOrdersPage = lazy(() => import("@/pages/PreOrdersPage"));
const CategoriesPage = lazy(() => import("@/pages/CategoriesPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const ShippingRefundPage = lazy(() => import("@/pages/ShippingRefundPage"));
const CollectionRedirect = lazy(() => import("@/pages/CollectionRedirect"));
const CollectionPage = lazy(() => import("@/pages/CollectionPage"));
const ReceiptShortLinkPage = lazy(() => import("@/pages/ReceiptShortLinkPage"));
const OGQuickPage = lazy(() => import("@/pages/OGQuickPage"));
const ShortcutRoutes = lazy(() =>
  import("@/pages/ShortcutRoutes").then((m) => ({ default: m.BestSellersRoute }))
);
const DealsRouteLazy = lazy(() =>
  import("@/pages/ShortcutRoutes").then((m) => ({ default: m.DealsRoute }))
);

// Heavy floating widgets — mount only after first paint / idle so they
// don't compete with the LCP element for main-thread time.
const WhatsAppButton = lazy(() => import("@/components/WhatsAppButton"));
const FlashSaleFloatingCTA = lazy(() => import("@/components/FlashSaleFloatingCTA"));

/** Suspense fallback intentionally invisible — a spinner would cause CLS
 *  and the pages themselves render their own skeletons. */
const RouteFallback = () => <div style={{ minHeight: "60vh" }} />;

function StorefrontWhatsApp() {
  // Render the floating WhatsApp button on every storefront route except
  // pages that already have their own focused UI (admin, POS, full-screen
  // chat, checkout flow). Keeps the bottom-left corner clean where it
  // matters and visible where it can actually drive conversations.
  //
  // Phone number is read from `/api/settings` so the admin can change it
  // in Site Settings without a redeploy. Falls back to the seeded default
  // while the fetch is in flight so the button never disappears.
  //
  // We also delay the entire fetch behind `requestIdleCallback` so the
  // widget can't compete with LCP-critical requests on first load.
  const { pathname } = useLocation();
  const HIDE_ON = ["/admin", "/admin-portal", "/pos", "/checkout", "/ogquick", "/chat"];
  const [phone, setPhone] = useState("8688462189");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;
    let idleHandle = null;
    let fallbackTimer = null;
    const load = () => {
      if (!alive) return;
      setReady(true);
      publicData('settings')
        .then((d) => {
          if (!alive || !d) return;
          const n = (d.whatsapp_number || "").trim();
          if (n) setPhone(n);
        })
        .catch(() => {});
    };
    // Wait for the browser to be idle so the widget mount + fetch don't
    // steal from LCP. Uses a safe wrapper so calling with the bare
    // reference doesn't lose the `this=window` binding.
    if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
      try {
        idleHandle = window.requestIdleCallback(load);
      } catch (_) {
        fallbackTimer = setTimeout(load, 1500);
      }
    } else {
      fallbackTimer = setTimeout(load, 1500);
    }
    return () => {
      alive = false;
      if (idleHandle && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleHandle);
      }
      if (fallbackTimer) clearTimeout(fallbackTimer);
    };
  }, []);

  if (HIDE_ON.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return null;
  if (!phone || !ready) return null;
  return (
    <Suspense fallback={null}>
      <WhatsAppButton phone={phone} />
    </Suspense>
  );
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        else window.scrollTo(0, 0);
      }, 80);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
}

function PageViewTracker() {
  const { pathname, search, key } = useLocation();
  useLayoutEffect(() => { trackPageView(pathname + search, key); }, [pathname, search, key]);
  useEffect(() => {
    if (pathname.startsWith("/admin") || pathname.startsWith("/account")) return;
    const API = process.env.REACT_APP_BACKEND_URL;
    // Fire tracking behind rIC so it never competes with LCP-critical
    // requests on the first paint.
    const send = () => {
      let sessionId = localStorage.getItem("og_sid");
      if (!sessionId) {
        sessionId =
          typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : Math.random().toString(36).slice(2) + Date.now();
        localStorage.setItem("og_sid", sessionId);
      }
      axios
        .post(`${API}/api/track/pageview`, {
          path: pathname,
          referrer: document.referrer || "",
          session_id: sessionId,
        })
        .catch(() => {});
    };
    const w = typeof window !== "undefined" ? window : {};
    if (typeof w.requestIdleCallback === "function") {
      try {
        w.requestIdleCallback(send);
      } catch (_) {
        setTimeout(send, 2000);
      }
    } else {
      setTimeout(send, 2000);
    }
  }, [pathname]);
  return null;
}

// AppRoutes: intercepts the Emergent-managed Google Auth callback fragment
// (`#session_id=<id>`) synchronously during render — must happen BEFORE
// the normal <Routes> tree mounts, otherwise the AuthProvider's initial
// /auth/me hit races the session-exchange and produces a false 401 flash.
//
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRoutes() {
  const location = useLocation();
  if (location.hash && location.hash.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/shop" element={<ShopPage />} />
      <Route path="/machines" element={<MachineLocatorPage />} />
      <Route path="/product/:id" element={<ProductPage />} />
      <Route path="/products/:id" element={<ProductPage />} />
      <Route path="/collections" element={<CollectionRedirect />} />
      <Route path="/collections/:handle" element={<CollectionPage />} />
      <Route path="/cart" element={<CartPage />} />
      <Route path="/checkout" element={<CheckoutPage />} />
      <Route path="/balance-pay/:token" element={<BalancePayPage />} />
      <Route path="/order/success" element={<OrderSuccessPage />} />
      <Route path="/orders/:id" element={<OrderSuccessPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/account" element={<AccountPage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/admin-portal" element={<AdminPage />} />
      <Route path="/pos" element={<POSStandalonePage />} />
      <Route path="/pos/login" element={<POSStandalonePage />} />
      <Route path="/r/:shortId" element={<ReceiptShortLinkPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/request-product" element={<ProductRequestPage />} />
      <Route path="/brands" element={<BrandsPage />} />
      <Route path="/pre-orders" element={<PreOrdersPage />} />
      <Route path="/preorders" element={<PreOrdersPage />} />
      <Route path="/categories" element={<CategoriesPage />} />
      <Route path="/best-sellers" element={<ShortcutRoutes />} />
      <Route path="/deals" element={<DealsRouteLazy />} />
      <Route path="/ogquick" element={<OGQuickPage />} />
      <Route path="/chat" element={<OGQuickPage />} />
      <Route path="/policies/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/policies/terms" element={<TermsPage />} />
      <Route path="/policies/shipping-refund" element={<ShippingRefundPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}


function App() {
  // Prime the logo manifest as early as possible so brandLogoUrl /
  // categoryLogoUrl can attach `?v=<mtime>` cache busters on first
  // render. Cheap (<10KB, 60s cached server-side + sessionStorage).
  useEffect(() => {
    ensureLogoManifest();
  }, []);  return (
    <HelmetProvider>
      {/* SEO tags are managed page-by-page via <SEOHead>. Keeping the
          root <Helmet> minimal — just site-wide constants that never
          change per route — prevents duplicate title/description tags
          from leaking into the DOM during hydration. */}
      <AuthProvider>
        <CartProvider>
          <div className="App bg-white min-h-screen">
            <BrowserRouter>
              <RouteSEO />
              <ScrollToTop />
              <PageViewTracker />
              <CartAddedToast />
              <Toaster position="top-center" richColors />
              <SiteLiveGate>
                <Suspense fallback={<RouteFallback />}>
                  <AppRoutes />
                </Suspense>
                <StorefrontWhatsApp />
                <Suspense fallback={null}>
                  <FlashSaleFloatingCTA />
                </Suspense>
              </SiteLiveGate>
            </BrowserRouter>
          </div>
        </CartProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
